import { useLayoutEffect } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { apiRequestResponse } from "@/lib/queryClient";

/**
 * AutoTranslate walks the live DOM and machine-translates every visible text
 * node into the currently selected language via the backend /api/translate
 * endpoint (which caches results in Postgres). This lets the entire app render
 * in any supported language without rewriting each page.
 *
 * Besides plain text nodes it also translates input/textarea placeholders,
 * aria-label / title attributes, <option> labels, and the document title.
 * Strings that fail to translate (network error, rate limit) are retried with
 * exponential backoff instead of being abandoned for the session.
 *
 * Mount it once, high in the tree, inside LanguageProvider. Uncached visible
 * text keeps its layout but is briefly masked while translations load.
 */

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "KBD", "SAMP",
  "TEXTAREA", "INPUT", "SELECT", "SVG", "PATH", "CANVAS",
]);

// Translatable attributes, checked on every element.
const TRANSLATE_ATTRS = ["placeholder", "aria-label", "title"] as const;

// Per-request caps. Kept well under the server's global 100kb JSON body limit
// (and its own per-request budget) so requests never get rejected for size.
// 30 matches the server's Gemini sub-batch size, so each request maps to one
// model call and CLIENT_CONCURRENCY bounds total provider concurrency.
const MAX_BATCH_COUNT = 30;
const MAX_BATCH_CHARS = 8000;
const DEBOUNCE_MS = 40;
const PENDING_ATTR = "data-auto-translate-pending";
const LOADING_ATTR = "data-auto-translate-loading";
const MAX_MASK_MS = 8_000;
const HAS_LETTER = /\p{L}/u;

// Retry policy for strings that failed to translate. Exponential backoff,
// bounded attempts — so transient failures (network blips, 429s) self-heal
// without a reload, but a permanently-failing string eventually goes quiet.
const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 60_000;
const MAX_ATTEMPTS = 6;

interface FailInfo {
  attempts: number;
  nextRetryAt: number;
}

// In-memory translation cache per language, hydrated from localStorage.
const memCache: Record<string, Map<string, string>> = {};

// Strings that failed to translate this session, with retry bookkeeping.
// Kept in memory only (not persisted) so a fresh page load starts clean.
const failedCache: Record<string, Map<string, FailInfo>> = {};

function getFailed(lang: string): Map<string, FailInfo> {
  return (failedCache[lang] ??= new Map<string, FailInfo>());
}

function loadCache(lang: string): Map<string, string> {
  if (memCache[lang]) return memCache[lang];
  const map = new Map<string, string>();
  try {
    const raw = localStorage.getItem(`zeastro:tr:${lang}`);
    if (raw) {
      const obj = JSON.parse(raw) as Record<string, string>;
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === "string" && v.length > 0 && v !== k) map.set(k, v);
      }
    }
  } catch {
    // ignore corrupt cache
  }
  memCache[lang] = map;
  return map;
}

function persistCache(lang: string) {
  try {
    const map = memCache[lang];
    if (!map) return;
    const obj: Record<string, string> = {};
    map.forEach((v, k) => {
      obj[k] = v;
    });
    localStorage.setItem(`zeastro:tr:${lang}`, JSON.stringify(obj));
  } catch {
    // storage full / disabled — translations still work in-memory
  }
}

function isTranslatable(core: string): boolean {
  return core.length >= 2 && HAS_LETTER.test(core);
}

export default function AutoTranslate() {
  const { language, languageSwitchId, completeLanguageSwitch } = useLanguage();

  useLayoutEffect(() => {
    const lang = language;
    const enabled = !!lang && lang !== "en";
    // Per-effect record of which nodes we mutated so we can restore on cleanup.
    const state = new WeakMap<Text, { origFull: string; outFull: string }>();
    // attr name -> { orig, out } per element we mutated.
    const attrState = new WeakMap<Element, Record<string, { orig: string; out: string }>>();
    const mutatedElements = new Set<Element>();
    let titleState: { orig: string; out: string } | null = null;
    if (!enabled) {
      const frame = window.requestAnimationFrame(() =>
        completeLanguageSwitch?.(languageSwitchId),
      );
      return () => window.cancelAnimationFrame(frame);
    }

    const cache = loadCache(lang);
    const failed = getFailed(lang);
    let timer: number | undefined;
    let retryTimer: number | undefined;
    let fetching = false;
    let cancelled = false;
    let switchSettled = false;
    const maskedElements = new Set<Element>();
    const maskExpired = new WeakSet<Element>();
    let maskTimer: number | undefined;

    function clearMasks(expired = false) {
      maskedElements.forEach((el) => {
        if (expired) maskExpired.add(el);
        el.removeAttribute(PENDING_ATTR);
      });
      maskedElements.clear();
      document.documentElement.removeAttribute(LOADING_ATTR);
      if (maskTimer) window.clearTimeout(maskTimer);
      maskTimer = undefined;
    }

    function syncMasks(targets: Set<Element>) {
      maskedElements.forEach((el) => {
        if (!targets.has(el)) {
          el.removeAttribute(PENDING_ATTR);
          maskedElements.delete(el);
        }
      });
      targets.forEach((el) => {
        if (!maskExpired.has(el)) {
          el.setAttribute(PENDING_ATTR, "");
          maskedElements.add(el);
        }
      });
      if (maskedElements.size === 0) {
        clearMasks();
      } else {
        document.documentElement.setAttribute(LOADING_ATTR, "");
        // Translation outages must not leave controls hidden indefinitely.
        if (!maskTimer) maskTimer = window.setTimeout(() => clearMasks(true), MAX_MASK_MS);
      }
    }

    function settleSwitch() {
      if (cancelled || switchSettled) return;
      switchSettled = true;
      window.requestAnimationFrame(() =>
        completeLanguageSwitch?.(languageSwitchId),
      );
    }

    function canTry(core: string): boolean {
      const f = failed.get(core);
      if (!f) return true;
      return f.attempts < MAX_ATTEMPTS && f.nextRetryAt <= Date.now();
    }

    function markFailed(core: string, minDelayMs?: number) {
      const f = failed.get(core) ?? { attempts: 0, nextRetryAt: 0 };
      f.attempts += 1;
      const backoff = Math.min(RETRY_BASE_MS * 2 ** (f.attempts - 1), RETRY_MAX_MS);
      f.nextRetryAt = Date.now() + Math.max(backoff, minDelayMs ?? 0);
      failed.set(core, f);
    }

    function scheduleRetry() {
      if (cancelled) return;
      let earliest = Infinity;
      failed.forEach((f) => {
        if (f.attempts < MAX_ATTEMPTS && f.nextRetryAt < earliest) {
          earliest = f.nextRetryAt;
        }
      });
      if (!Number.isFinite(earliest)) return;
      const delay = Math.max(50, earliest - Date.now());
      if (retryTimer) window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(run, delay);
    }

    function shouldSkip(node: Text): boolean {
      const parent = node.parentElement;
      if (!parent) return true;
      // OPTION text is translatable; its ancestors SELECT/OPTGROUP are not
      // walked as parents of the text node so this works naturally.
      if (parent.tagName !== "OPTION" && SKIP_TAGS.has(parent.tagName)) return true;
      if (parent.isContentEditable) return true;
      if (parent.closest("[data-no-translate]")) return true;
      return false;
    }

    function elementSkipped(el: Element): boolean {
      return !!el.closest("[data-no-translate]") || SKIP_TAGS.has(el.tagName) && el.tagName !== "INPUT" && el.tagName !== "TEXTAREA" && el.tagName !== "SELECT";
    }

    function collect(): Text[] {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      const nodes: Text[] = [];
      let n = walker.nextNode();
      while (n) {
        const t = n as Text;
        const core = (t.nodeValue ?? "").trim();
        if (isTranslatable(core) && !shouldSkip(t)) {
          nodes.push(t);
        }
        n = walker.nextNode();
      }
      return nodes;
    }

    // Apply cached translations to text nodes; add cache misses to `pending`.
    function applyTextNodes(pending: Set<string>, mutate: boolean, masks?: Set<Element>) {
      for (const t of collect()) {
        const v = t.nodeValue ?? "";
        const prev = state.get(t);
        if (prev && prev.outFull === v) continue; // already translated, untouched
        const core = v.trim();
        const tr = cache.get(core);
        if (tr && tr !== core) {
          if (!mutate) continue;
          const leadWs = v.match(/^\s*/)?.[0] ?? "";
          const trailWs = v.match(/\s*$/)?.[0] ?? "";
          const outFull = leadWs + tr + trailWs;
          state.set(t, { origFull: v, outFull });
          t.nodeValue = outFull;
        } else if (!tr && canTry(core)) {
          pending.add(core);
          if (t.parentElement) masks?.add(t.parentElement);
        }
      }
    }

    // Apply cached translations to placeholder/aria-label/title attributes.
    function applyAttributes(pending: Set<string>, mutate: boolean, masks?: Set<Element>) {
      const selector = TRANSLATE_ATTRS.map((a) => `[${a}]`).join(",");
      const els = document.body.querySelectorAll(selector);
      els.forEach((el) => {
        if (elementSkipped(el)) return;
        for (const attr of TRANSLATE_ATTRS) {
          const v = el.getAttribute(attr);
          if (v == null) continue;
          const rec = attrState.get(el)?.[attr];
          if (rec && rec.out === v) continue; // already translated
          const core = v.trim();
          if (!isTranslatable(core)) continue;
          const tr = cache.get(core);
          if (tr && tr !== core) {
            if (!mutate) continue;
            const recs = attrState.get(el) ?? {};
            recs[attr] = { orig: v, out: tr };
            attrState.set(el, recs);
            mutatedElements.add(el);
            el.setAttribute(attr, tr);
          } else if (!tr && canTry(core)) {
            pending.add(core);
            if (attr === "placeholder") masks?.add(el);
          }
        }
      });
    }

    // Apply cached translation to the browser tab title.
    function applyTitle(pending: Set<string>, mutate: boolean) {
      const cur = document.title;
      if (titleState && cur === titleState.out) return;
      const core = cur.trim();
      if (!isTranslatable(core)) return;
      const tr = cache.get(core);
      if (tr && tr !== core) {
        if (!mutate) return;
        titleState = { orig: cur, out: tr };
        document.title = tr;
      } else if (!tr && canTry(core)) {
        pending.add(core);
      }
    }

    // Scan the page. With mutate=true, paint everything the cache can
    // translate; with mutate=false, only report what's still missing.
    // Returns the set of strings needing a fetch/retry.
    function apply(mutate = true): Set<string> {
      const pending = new Set<string>();
      const masks = mutate ? new Set<Element>() : undefined;
      applyTextNodes(pending, mutate, masks);
      applyAttributes(pending, mutate, masks);
      applyTitle(pending, mutate);
      if (masks) syncMasks(masks);
      return pending;
    }

    async function fetchPending(pending: Set<string>) {
      if (cancelled || fetching || pending.size === 0) return;
      const todo = Array.from(pending).filter((s) => !cache.has(s) && canTry(s));
      if (todo.length === 0) {
        // Nothing fetchable (all misses are cooling down) — still paint what
        // the cache does have so the page isn't left in English.
        apply();
        return;
      }
      fetching = true;
      try {
        // Build count- and byte-budgeted chunks up front…
        const chunks: string[][] = [];
        let i = 0;
        while (i < todo.length) {
          const chunk: string[] = [];
          let chars = 0;
          while (
            i < todo.length &&
            chunk.length < MAX_BATCH_COUNT &&
            (chunk.length === 0 || chars + todo[i].length <= MAX_BATCH_CHARS)
          ) {
            chars += todo[i].length;
            chunk.push(todo[i]);
            i++;
          }
          if (chunk.length === 0) break;
          chunks.push(chunk);
        }

        // …then fetch them with limited parallelism. The complete set is
        // applied together after all chunks land so a language switch does
        // not leave the page in a visibly mixed state.
        async function fetchChunk(chunk: string[]) {
          let res: Response;
          try {
            res = await apiRequestResponse("POST", "/api/translate", {
              lang,
              texts: chunk,
            });
          } catch {
            for (const s of chunk) markFailed(s);
            return;
          }
          if (cancelled) return;
          if (!res.ok) {
            // Honor Retry-After on rate-limit rejections so retries line up
            // with when the server will actually accept them.
            let minDelay: number | undefined;
            if (res.status === 429) {
              const ra = Number(res.headers?.get?.("Retry-After"));
              if (Number.isFinite(ra) && ra > 0) minDelay = ra * 1000;
            }
            for (const s of chunk) markFailed(s, minDelay);
            return;
          }
          const data = (await res.json().catch(() => null)) as
            | { translations?: Record<string, string> }
            | null;
          if (!data?.translations) {
            for (const s of chunk) markFailed(s);
            return;
          }
          let gotAny = false;
          for (const s of chunk) {
            const out = data.translations[s];
            if (typeof out === "string" && out.length > 0 && out !== s) {
              cache.set(s, out);
              failed.delete(s);
              gotAny = true;
            } else {
              markFailed(s);
            }
          }
        // No per-chunk paint: the whole scan is applied in one pass after
        // every chunk lands, so the nav, sidebar, and content flip together.
          void gotAny;
        }

        const CONCURRENCY = 4;
        let next = 0;
        async function worker() {
          while (next < chunks.length && !cancelled) {
            const chunk = chunks[next++];
            await fetchChunk(chunk);
          }
        }
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, worker),
        );
        persistCache(lang);
        if (!cancelled) apply();
        settleSwitch();
      } finally {
        fetching = false;
        scheduleRetry();
        // A React page can add content while the current batch is in flight.
        // Re-scan after the batch releases the lock so newly loaded cards
        // cannot remain in English until a full page refresh.
        if (!cancelled) {
          const remaining = apply(false);
          if (remaining.size > 0) void fetchPending(remaining);
        }
      }
    }

    function run() {
      if (cancelled) return;
      // Cached copy is ready now; do not hold it behind unrelated cache misses.
      // Uncached visible copy is masked until the current language is ready.
      const pending = apply(true);
      if (pending.size > 0) {
        void fetchPending(pending);
      } else {
        apply(true);
        settleSwitch();
        scheduleRetry();
      }
    }

    function schedule() {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(run, DEBOUNCE_MS);
    }

    function restoreAll() {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let n = walker.nextNode();
      while (n) {
        const t = n as Text;
        const prev = state.get(t);
        if (prev && t.nodeValue === prev.outFull) {
          t.nodeValue = prev.origFull;
        }
        n = walker.nextNode();
      }
      mutatedElements.forEach((el) => {
        const recs = attrState.get(el);
        if (!recs) return;
        for (const [attr, rec] of Object.entries(recs)) {
          if (el.getAttribute(attr) === rec.out) {
            el.setAttribute(attr, rec.orig);
          }
        }
      });
      if (titleState && document.title === titleState.out) {
        document.title = titleState.orig;
      }
    }

    const observer = new MutationObserver(() => {
      if (cancelled) return;
      // Mutation observers run before paint. Translate reused/new route nodes
      // immediately, including content arriving after a lazy page has mounted.
      apply(true);
      schedule();
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [...TRANSLATE_ATTRS],
    });
    run();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      if (retryTimer) window.clearTimeout(retryTimer);
      observer.disconnect();
      clearMasks();
      restoreAll();
    };
  }, [language, languageSwitchId, completeLanguageSwitch]);

  return (
    <div
      className="auto-translation-progress"
      data-no-translate
      role="progressbar"
      aria-label="Loading translations"
    />
  );
}
